// frontend/src/pages/LinhaTempo.js - VERSÃO ATUALIZADA E INTEGRADA

import React, { useState } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert,
  CircularProgress, Paper, Stepper, Step, StepLabel, StepContent, Chip,
  Grid, Divider, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  List, ListItem, ListItemText, ListItemIcon
} from '@mui/material';
import {
  Search as SearchIcon, Timeline as TimelineIcon, Business as BusinessIcon,
  Schedule as ScheduleIcon, LocalShipping as ShippingIcon, Store as StoreIcon,
  Info as InfoIcon, Refresh as RefreshIcon, QrCodeScanner as ScannerIcon,
  Science as LabIcon, Error as ErrorIcon, CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

// Mapeamento de ícones para cada tipo de evento da timeline
const getStepIcon = (etapa) => {
  if (etapa.includes('expedido')) return <ShippingIcon />;
  if (etapa.includes('recebido')) return <StoreIcon />;
  if (etapa.includes('erro')) return <ErrorIcon />;
  if (etapa.includes('producao')) return <LabIcon />;
  return <BusinessIcon />;
};

const LinhaTempo = () => {
  const [codigoRequisicao, setCodigoRequisicao] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dados, setDados] = useState(null); // Estado para armazenar a resposta completa
  const [dialogDetalhes, setDialogDetalhes] = useState(false);
  const [etapaSelecionada, setEtapaSelecionada] = useState(null);

  const { api } = useAuth();

  const buscarLinhaTempo = async () => {
    if (!codigoRequisicao.trim()) {
      setError('Por favor, digite o código da requisição');
      return;
    }

    setLoading(true);
    setError('');
    setDados(null);

    try {
      const response = await api.buscarLinhaTempo(codigoRequisicao.trim());

      // Aceita dados em qualquer formato da API
      const temDados =
        (response.success && response.data?.timeline_completa?.length > 0) ||
        (response.success && Array.isArray(response.dados) && response.dados.length > 0) ||
        (response.success && Array.isArray(response.timeline) && response.timeline.length > 0);

      if (temDados) {
        const dadosParaSalvar = response.dados ? response : (response.data || response);
        setDados(dadosParaSalvar);
      } else {
        setError(response.message || 'Nenhum dado encontrado para esta requisição.');
      }

    } catch (err) {
      setError(err.message || 'Erro ao buscar dados da requisição');
    } finally {
      setLoading(false);
    }
  };



  const mostrarDetalhes = (etapa) => {
    setEtapaSelecionada(etapa);
    setDialogDetalhes(true);
  };
  
  // Suporta ambos formatos: dados (antigo) ou data.timeline_completa (novo)
  let dadosLinhaTempoRaw =
    (Array.isArray(dados?.timeline_completa) && dados?.timeline_completa) ||
    (Array.isArray(dados?.data?.timeline_completa) && dados?.data?.timeline_completa) ||
    (Array.isArray(dados?.dados) && dados?.dados) ||
    (Array.isArray(dados?.timeline) && dados?.timeline) ||
    [];

  // Transforma os dados crus em objetos formatados para o Stepper
  const dadosLinhaTempo = dadosLinhaTempoRaw.map((etapa, idx) => ({
    // Se já está formatado, retorna direto
    ...(etapa.titulo ? etapa : {}),
    // Se não está formatado, cria campos padrão
    titulo: etapa.titulo || `Movimentação - ${etapa.setor || 'N/A'}`,
    descricao: etapa.descricao || etapa.setor || 'Sem descrição',
    etapa: etapa.etapa || 'lab_producao',
    cor: etapa.cor || 'info',
    icone: etapa.icone || '🧪',
    funcionario: etapa.funcionario || 'Funcionário não identificado',
    funcionario_codigo: etapa.codigo_funcionario || etapa.funcionario_codigo || 'N/A',
    setor: etapa.setor || 'N/A',
    origem: etapa.origem || 'LAB',
    data_formatada: etapa.data_formatada || etapa.data_original || etapa.data || '',
    tempo_desde_anterior: etapa.tempo_desde_anterior || null,
    ordem: idx + 1,
    detalhes: etapa.detalhes || {},
    ...etapa // mantém todos os campos originais
  }));

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: 2 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <TimelineIcon sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">🔍 Linha do Tempo</Typography>
            <Typography variant="subtitle1" opacity={0.9}>Rastreamento completo da requisição (LAB + PCP)</Typography>
          </Box>
        </Box>
      </Paper>

      <Card elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">📝 Buscar Requisição</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={10}>
            <TextField
              fullWidth
              label="Código da Requisição"
              value={codigoRequisicao}
              onChange={(e) => setCodigoRequisicao(e.target.value)}
              placeholder="Digite o código completo ou parcial..."
              InputProps={{ startAdornment: <ScannerIcon sx={{ mr: 1, color: '#64748b' }} /> }}
              onKeyPress={(e) => { if (e.key === 'Enter') buscarLinhaTempo(); }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="contained" onClick={buscarLinhaTempo} disabled={loading} sx={{ height: 56, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              {loading ? <CircularProgress size={24} color="inherit" /> : <SearchIcon />}
            </Button>
          </Grid>
        </Grid>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {dados && (
        <Card elevation={0} sx={{ p: 3, borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">📋 Histórico da Requisição: {dados.medicacao_pcp?.codigo || dados.codigo_info?.original}</Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Chip
                      icon={<CheckCircleIcon />}
                      label={`Status: ${dados.status_atual?.descricao || 'Não definido'}`}
                      color="success"
                      variant="outlined"
                  />
                  <Tooltip title="Atualizar dados">
                    <IconButton onClick={buscarLinhaTempo} disabled={loading}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
            </Box>
            
            {/* Informações da medicação PCP */}
            {dados.medicacao_pcp && (
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="info.dark" gutterBottom>
                  📦 Informações da Medicação (PCP)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Código:</Typography>
                    <Typography variant="body2" fontWeight="bold">{dados.medicacao_pcp.codigo}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Quantidade:</Typography>
                    <Typography variant="body2" fontWeight="bold">{dados.medicacao_pcp.quantidade}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Origem:</Typography>
                    <Typography variant="body2" fontWeight="bold">{dados.medicacao_pcp.filial_origem?.nome}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Destino:</Typography>
                    <Typography variant="body2" fontWeight="bold">{dados.medicacao_pcp.filial_destino?.nome}</Typography>
                  </Grid>


                </Grid>
              </Paper>
            )}

            <Divider sx={{ mb: 3 }} />

            <Stepper orientation="vertical">
            {dadosLinhaTempo.map((etapa, index) => (
              <Step key={index} active={true}>
                <StepLabel

                  StepIconComponent={() => (
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40,
                      borderRadius: '50%',
                      backgroundColor: etapa.cor === 'danger' ? 'error.main' :
                                       etapa.cor === 'warning' ? 'warning.main' :
                                       etapa.cor === 'success' ? 'success.main' : 'primary.main',
                      color: 'white',
                      boxShadow: 3
                    }}>
                      {getStepIcon(etapa.etapa)}
                    </Box>
                  )}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">{etapa.titulo}</Typography>
                      <Typography variant="body2" color="textSecondary">{etapa.data_formatada}</Typography>
                    </Box>
                    <Chip label={etapa.origem} size="small" color={etapa.origem === 'PCP' ? 'secondary' : 'info'} />
                  </Box>
                </StepLabel>
                <StepContent>
                  <Paper elevation={1} sx={{ 
                    p: 2, 
                    bgcolor: 'grey.50', 
                    borderLeft: `4px solid`, 
                    borderColor: etapa.cor === 'danger' ? 'error.main' :
                                 etapa.cor === 'warning' ? 'warning.main' :
                                 etapa.cor === 'success' ? 'success.main' : 'primary.main'
                  }}>
                      <Typography variant="body2" gutterBottom>
                        <strong>Funcionário:</strong> {etapa.funcionario}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Setor:</strong> {etapa.setor}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Descrição:</strong> {etapa.descricao}
                      </Typography>

                      {etapa.tempo_desde_anterior && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                              <strong>⏱️ Tempo desde a etapa anterior:</strong> {etapa.tempo_desde_anterior}
                          </Typography>
                      )}
                      <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => mostrarDetalhes(etapa)}>
                        Ver Detalhes
                      </Button>
                  </Paper>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {/* Estatísticas da Timeline */}
          {dados.estatisticas && (
            <Paper sx={{ p: 2, mt: 3, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>📊 Estatísticas da Timeline</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Total de Eventos:</Typography>
                  <Typography variant="body2" fontWeight="bold">{dados.estatisticas.total_eventos}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Eventos LAB:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="info.main">{dados.estatisticas.eventos_lab}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Eventos PCP:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="secondary.main">{dados.estatisticas.eventos_pcp}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Período Total:</Typography>
                  <Typography variant="body2" fontWeight="bold">{dados.estatisticas.periodo_total}</Typography>
                </Grid>
              </Grid>
            </Paper>
          )}
        </Card>
      )}

      <Dialog open={dialogDetalhes} onClose={() => setDialogDetalhes(false)} maxWidth="sm" fullWidth>
        <DialogTitle><Box display="flex" alignItems="center" gap={2}><InfoIcon color="primary" /><Typography variant="h6">Detalhes da Etapa</Typography></Box></DialogTitle>
        <DialogContent>
          {etapaSelecionada && (
            <List>
              <ListItem><ListItemText primary="TÍTULO" secondary={etapaSelecionada.titulo || 'N/A'} /></ListItem>
              <ListItem><ListItemText primary="FUNCIONÁRIO" secondary={`${etapaSelecionada.funcionario} (${etapaSelecionada.funcionario_codigo})`} /></ListItem>
              <ListItem><ListItemText primary="SETOR" secondary={etapaSelecionada.setor || 'N/A'} /></ListItem>
              <ListItem><ListItemText primary="DATA/HORA" secondary={etapaSelecionada.data_formatada || 'N/A'} /></ListItem>
              <ListItem><ListItemText primary="ORIGEM DO DADO" secondary={etapaSelecionada.origem || 'N/A'} /></ListItem>
              <ListItem><ListItemText primary="TIPO DE EVENTO" secondary={etapaSelecionada.tipo || 'N/A'} /></ListItem>
              {etapaSelecionada.detalhes?.motivo && (
                <ListItem>
                  <ListItemText primary="MOTIVO DO ERRO" secondary={etapaSelecionada.detalhes.motivo} />
                </ListItem>
              )}
              {etapaSelecionada.detalhes?.filial_destino && (
                <ListItem>
                  <ListItemText primary="FILIAL DESTINO" secondary={etapaSelecionada.detalhes.filial_destino} />
                </ListItem>
              )}
              {etapaSelecionada.detalhes?.cargo_usuario && (
                <ListItem>
                  <ListItemText primary="CARGO DO USUÁRIO" secondary={etapaSelecionada.detalhes.cargo_usuario} />
                </ListItem>
              )}
            </List>
          )}
        </DialogContent>
      </Dialog>


    </Box>
  );
};

export default LinhaTempo;
